import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth/auth.service';
import { catchError, switchMap, throwError } from 'rxjs';

/**
 * Authentication Interceptor
 * Automatically adds authentication token to outgoing HTTP requests
 * and handles token refresh on 401 errors
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const token = authService.getToken();

  // Skip adding token for authentication & external endpoints
  const isExternalEndpoint =
    req.url.includes('postalpincode') ||
    req.url.includes('/auth/login') ||
    req.url.includes('/auth/register') ||
    req.url.includes('/auth/signup') ||
    req.url.includes('/auth/verify-otp') ||
    req.url.includes('/auth/refresh') ||
    req.url.includes('/auth/forgot-password') ||
    req.url.includes('/auth/reset-password');

  let clonedRequest = req;

  if (token && !isExternalEndpoint) {
    // Clone the request and add authorization header
    clonedRequest = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  return next(clonedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      // If we get a 401 error and it's not an auth endpoint, try to refresh the token
      if (error.status === 401 && !isExternalEndpoint) {
        return authService.refreshToken().pipe(
          switchMap(() => {
            // Retry the original request with the new token
            const newToken = authService.getToken();
            const retryRequest = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
              },
            });
            return next(retryRequest);
          }),
          catchError((refreshError) => {
            // If refresh fails, logout and propagate the error
            authService.logout();
            return throwError(() => refreshError);
          })
        );
      }

      // For other errors, just propagate them
      return throwError(() => error);
    })
  );
};
